import {
  Observable, catchError, of, shareReplay, tap,
} from 'rxjs';

import {
  AuthService,
  Container,
  DataService,
  Service,
  SubscriptionPlanService,
  UserTrackService,
} from '../../../../symphony';

import { SlDataService } from '../sl-data.service';
import {
  SlAISolutionsContext,
  SolutionActionType,
  SolutionContext,
  aISolutionErrorMessages,
} from '../../global-constants';
import { SlApiService } from '../sl-api.service';
import { SlTrackingService } from './sl-tracking.service';
import {
  CloseAISolutionsClickSource,
  GetSolutionClickSource,
  IAnswerExplanationData,
  IQuestionData,
  ISolution,
} from '../../global-interface';
import { IMaterialHeader, MaterialType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { ICode } from '../../../../api/public/code-assistant';
import { DynamicPaywallLoader } from '../../../../_paywalls/public/DynamicPaywallLoader';
import { Touchpoint } from '../../../../api/public/subscription-api';

@Service()
export class SlAISolutionsService {
  private apiService = Container.take(SlAISolutionsContext, SlApiService);

  private dataService = Container.take(SlAISolutionsContext, SlDataService);

  private globalDataService = Container.take('global', DataService);

  private userTrackService = Container.take('global', UserTrackService);

  private subscriptionPlanService = Container.take('global', SubscriptionPlanService);

  private trackingService = Container.take(
    SlAISolutionsContext,
    SlTrackingService,
  );

  private paywallLoader = DynamicPaywallLoader.ensureRegistered();

  constructor() {
    this.subscriptionPlanService.subscriptionPlanConfigs.pipe(
      tap(() => { this.syncAttempts(); })
    ).subscribe();

    this.userTrackService.userTrack.pipe(
      tap(track => {
        if (this.dataService.attemptsGlobalData$.value) {
          this.dataService.remainingAttemptsCountData$.next(
            this.dataService.attemptsGlobalData$.value.find((item) => item.track === track)?.contextsAttempts
          );
        }
      })
    ).subscribe();
  }

  private getCodeAssistantConfig = () => {
    if (this.dataService.codeAssistantConfig$.value) {
      return;
    }

    this.apiService
      .getCodeAssistantConfig()
      .pipe(
        tap((configRes) => {
          this.dataService.codeAssistantConfig$.next(configRes);
        }),
        catchError(() => of(null)),
      )
      .subscribe(() => { });
  };

  private getAttemptData = () => {
    const userTrack = this.userTrackService.getUserTrack();
    if (this.dataService.attemptsGlobalData$.value) {
      this.dataService.remainingAttemptsCountData$.next(
        (this.dataService.attemptsGlobalData$.value.find((item) => item.track === userTrack))?.contextsAttempts,
      );
      return;
    }

    this.apiService
      .getAttemptData()
      .pipe(
        tap((attemptData) => {
          this.dataService.attemptsGlobalData$.next(attemptData);
          this.dataService.remainingAttemptsCountData$.next(
            (attemptData.find((item) => item.track === userTrack) || attemptData[0])?.contextsAttempts,
          );
        }),
        catchError(() => of(null)),
        shareReplay(1),
      )
      .subscribe(() => { });
  };

  private informAboutSolutionView = (context: SolutionContext) => {
    this.apiService
      .use(context, this.userTrackService.getUserTrack())
      .pipe(
        tap((attemptData) => {
          const globalAttempts = [...this.dataService.attemptsGlobalData$.value];
          const newGlobalAttempts = globalAttempts.map(globalTrack => {
            const responseTrack = attemptData.find(a => a.track === globalTrack.track);
            globalTrack.contextsAttempts = globalTrack.contextsAttempts.map(ca => {
              const responseContext = responseTrack?.contextsAttempts.find(a => a.context === ca.context);
              if (!responseContext) return ca;
              return responseContext;
            });
            return globalTrack;
          });
          this.dataService.attemptsGlobalData$.next(newGlobalAttempts);
          this.dataService.remainingAttemptsCountData$.next(newGlobalAttempts.find(a => a.track === this.userTrackService.getUserTrack()).contextsAttempts);
        }),
        catchError(() => of(null)),
      )
      .subscribe(() => { });
  };

  private getTextFromHtml = (htmlString: string) => {
    if (!htmlString) return null;
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(htmlString, 'text/html');
    return htmlDoc.documentElement.textContent || '';
  };

  private explainTheCode = () => this.apiService.explainTheCode(
    this.dataService.codes$.value,
    this.userTrackService.getUserTrack()
  );

  private findBug = (problem: string) => this.apiService.findBug(
    this.dataService.codes$.value,
    problem,
    this.userTrackService.getUserTrack()
  );

  private hint = (problem: string) => this.apiService.hint(
    this.dataService.codes$.value,
    problem,
    this.userTrackService.getUserTrack()
  );

  private getSolution = (problem: string) => this.apiService.getSolution(
    problem,
    this.dataService.solution$.value?.encryptedCode,
    this.dataService.solution$.value?.languageId,
    this.userTrackService.getUserTrack()
  );

  private alternativeLessonExplanation = () => this.apiService.alternativeLessonExplanation(
    this.dataService.courseName$.value,
    this.dataService.lessonContent$.value,
    this.globalDataService.learningExperience$.value.alias,
  );

  private realLifeExamples = () => this.apiService.realLifeExamples(
    this.dataService.courseName$.value,
    this.dataService.lessonContent$.value,
    this.globalDataService.learningExperience$.value.alias,
  );

  private questionHint = () => this.apiService.questionHint(
    this.dataService.courseName$.value,
    this.dataService.questionData$.value,
    this.globalDataService.learningExperience$.value.alias,
  );

  private getAiSolutionObservalble = (actionId: SolutionActionType) => {
    const problem = this.getTextFromHtml(this.dataService.problem$.value);

    switch (actionId) {
      case SolutionActionType.ExplainCode:
        return this.explainTheCode();
      case SolutionActionType.FindBug:
        return this.findBug(problem);
      case SolutionActionType.Hint:
        return this.hint(problem);
      case SolutionActionType.GetSolution:
        return this.getSolution(problem);
      case SolutionActionType.AlternativeLessonExplanation:
        return this.alternativeLessonExplanation();
      case SolutionActionType.RealLifeExamples:
        return this.realLifeExamples();
      case SolutionActionType.QuestionHint:
        return this.questionHint();

      default:
        return of(null);
    }
  };

  private getAiSolution = (
    actionId: SolutionActionType,
    context: SolutionContext,
  ) => {
    const getAiSolutionObservalble = this.getAiSolutionObservalble(actionId);
    getAiSolutionObservalble
      .pipe(
        tap((res) => {
          this.dataService.aiSolutionsData$.next({
            ...this.dataService.aiSolutionsData$.value,
            [actionId]: res,
          });
          this.dataService.aiSolutionsErrors$.next({
            ...this.dataService.aiSolutionsErrors$.value,
            [actionId]: null,
          });
          this.informAboutSolutionView(context);
        }),
        catchError((err) => {
          this.dataService.aiSolutionsData$.next({
            ...this.dataService.aiSolutionsData$.value,
            [actionId]: null,
          });
          this.dataService.aiSolutionsErrors$.next({
            ...this.dataService.aiSolutionsErrors$.value,
            [actionId]:
              aISolutionErrorMessages[err?.[0]?.code]
              || aISolutionErrorMessages.notDefined,
          });
          return of(null);
        }),
      )
      .subscribe(() => {
        this.dataService.aiSolutionsLoading$.next({
          ...this.dataService.aiSolutionsLoading$.value,
          [actionId]: false,
        });
        this.dataService.aiSolutionsRate$.next({
          ...this.dataService.aiSolutionsRate$.value,
          [actionId]: null,
        });
      });
  };

  public handleAISolutionsMount = () => {
    this.dataService.isAnswerExplanationResWithError$.next(false);
    this.dataService.aiSolutionsIsOpenedState$.next({});
    this.dataService.aiSolutionsData$.next({});
    this.dataService.answerExplanationRes$.next('');
    this.getCodeAssistantConfig();
    this.getAttemptData();
  };

  public handleAISolutionsDataUpdate = (
    materialHeader: IMaterialHeader,
    codes: ICode[],
    problem: string,
    session: string,
    solution: ISolution,
    questionData: IQuestionData,
    answerExplanationData: IAnswerExplanationData,
    lessonContent: string,
    courseName: string,
    materialType: MaterialType,
  ): Observable<null> => {
    this.dataService.solution$.next(solution);
    this.dataService.problem$.next(problem);
    this.dataService.codes$.next(codes);
    this.dataService.materialHeader$.next(materialHeader);
    this.dataService.session$.next(session);
    this.dataService.questionData$.next(questionData);
    this.dataService.answerExplanationData$.next(answerExplanationData);
    this.dataService.lessonContent$.next(lessonContent);
    this.dataService.courseName$.next(courseName);
    this.dataService.aISolutionsMaterialType$.next(materialType);

    return of(null);
  };

  public aISolutionClickHandler = (
    actionId: SolutionActionType,
    getSolutionClickSource: GetSolutionClickSource,
    context: SolutionContext,
  ): Observable<null> => {
    const authService: AuthService = Container.take('global', AuthService);
    this.trackingService.trackGetSolutionClick(
      getSolutionClickSource,
      actionId,
      context,
    );

    const remainingAttemptsCountData = this.dataService.remainingAttemptsCountData$.value?.find(
      (data) => data.context === context,
    );

    if (
      !remainingAttemptsCountData.isUnlimited
      && remainingAttemptsCountData.remainingAttemptsCount < 1
    ) {
      this.paywallLoader.openPaywall({
        touchpoint: Touchpoint.kodiePlayground,
        props: {}
      });
      return of(null);
    }

    this.dataService.aiSolutionsLoading$.next({
      ...this.dataService.aiSolutionsLoading$.value,
      [actionId]: true,
    });
    this.getAiSolution(actionId, context);

    return of(null);
  };

  public handleAISolutionRate = (
    id: string,
    solutionRating: number,
    solutionType: number,
    context: SolutionContext,
  ): Observable<null> => {
    this.trackingService.trackSolutionRateClick(
      id,
      solutionRating,
      solutionType,
      context,
    );

    this.dataService.aiSolutionsRate$.next({
      ...this.dataService.aiSolutionsRate$.value,
      [solutionType]: solutionRating,
    });

    this.apiService
      .rateAISolution(id, solutionRating)
      .pipe(
        catchError(() => of(null)),
      )
      .subscribe();

    return of(null);
  };

  public handleAISolutionCollapse = (
    actionId: SolutionActionType,
    context: SolutionContext,
  ): Observable<null> => {
    const authService: AuthService = Container.take('global', AuthService);
    this.trackingService.trackAISolutionCollapse(
      actionId,
      !this.dataService.aiSolutionsIsOpenedState$.value[actionId],
      context,
    );
    const remainingAttemptsCountData = this.dataService.remainingAttemptsCountData$.value?.find(
      (data) => data.context === context,
    );

    if (
      !this.dataService.aiSolutionsIsOpenedState$.value[actionId]
      && !this.dataService.aiSolutionsData$.value[actionId]
      && !!remainingAttemptsCountData
      && !remainingAttemptsCountData.isUnlimited
      && remainingAttemptsCountData?.remainingAttemptsCount < 1
    ) {
      return of(null);
    }

    this.dataService.aiSolutionsIsOpenedState$.next({
      ...this.dataService.aiSolutionsIsOpenedState$.value,
      [actionId]:
        !this.dataService.aiSolutionsIsOpenedState$.value[actionId],
    });

    return of(null);
  };

  public handleAISolutionsClose = (
    actionId: SolutionActionType,
    closeAISolutionsClickSource: CloseAISolutionsClickSource,
  ): Observable<null> => {
    this.trackingService.trackCloseAISolutionsClick(
      actionId,
      closeAISolutionsClickSource,
    );

    const solutionsWithErrors = Object.keys(
      this.dataService.aiSolutionsErrors$.value,
    );
    solutionsWithErrors.forEach((solution) => {
      if (this.dataService.aiSolutionsErrors$.value[solution]) {
        this.dataService.aiSolutionsIsOpenedState$.next({
          ...this.dataService.aiSolutionsIsOpenedState$.value,
          [solution]: false,
        });
      }
    });

    return of(null);
  };

  public handleAISolutionsOpenHandler = (
    solutionContext: SolutionContext,
  ) => {
    if (solutionContext === SolutionContext.Practice) {
      this.trackingService.trackAISolutionsOpenClick();
    }

    if (solutionContext === SolutionContext.Lesson) {
      this.trackingService.trackKodiInLessonNavigationIconClick();
    }

    return of(null);
  };

  public handleAISolutionResponseCopy = (
    solutionContext: SolutionContext,
    actionId: SolutionActionType,
  ) => {
    if (solutionContext === SolutionContext.Lesson) {
      this.trackingService.trackAISolutionResponseCopy(actionId);
    }

    return of(null);
  };

  private checkPaywallRedirectionCaseInBottomsheet = (
    solutionContext: SolutionContext,
  ) => {
    const remainingAttemptsCountData = this.dataService.remainingAttemptsCountData$.value?.find(
      (data) => data.context === solutionContext,
    );

    if (
      !remainingAttemptsCountData.isUnlimited
      && remainingAttemptsCountData.remainingAttemptsCount < 1
    ) {
      this.paywallLoader.openPaywall({
        touchpoint: Touchpoint.kodieLesson,
        props: {}
      });
      return true;
    }

    return false;
  };

  public handleGetAIResponseInBottomSheetClick = (
    solutionContext: SolutionContext,
  ) => {
    this.trackingService.trackAnswerExplanationCTAClick();
    const shouldRedirect = this.checkPaywallRedirectionCaseInBottomsheet(solutionContext);
    if (shouldRedirect) {
      return of(null);
    }

    this.dataService.isAnswerExplanationResWithError$.next(false);
    this.dataService.isAnswerExplanationDataLoading$.next(true);
    this.apiService.answerExplanation(
      this.dataService.answerExplanationData$.value,
    ).then((response) => {
      if (
        (response as unknown as { status: number; }).status !== 200
        || !response.body
      ) {
        throw new Error('');
      }

      this.informAboutSolutionView(SolutionContext.Answer);
      const reader = response.body.getReader();
      const dataService = this.dataService;
      return new ReadableStream({
        start(controller) {
          const push = () => {
            reader.read().then(({ done, value }) => {
              if (done) {
                controller.close();
                return;
              }
              const decoder = new TextDecoder('utf-8');
              dataService.answerExplanationRes$.next(
                dataService.answerExplanationRes$.value
                + decoder.decode(value),
              );

              controller.enqueue(value);
              push();
            });
          };
          push();
        },
      });
    })
      .catch(() => {
        this.dataService.isAnswerExplanationResWithError$.next(true);
      })
      .finally(() => {
        this.dataService.isAnswerExplanationDataLoading$.next(false);
      });

    return of(null);
  };

  public syncAttempts = () => {
    this.dataService.attemptsGlobalData$.next(null);
    this.getAttemptData();
  };
}
